// services/mooringService.js

const API_URL = 'https://api.subtidal.com';

// --- helpers ---
const authHeaders = (token) => ({
  "Authorization": `Token ${token}`,
  "Content-Type": "application/json",
});

const buildUrl = (path, params = {}) => {
  const url = new URL(`${API_URL}${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      url.searchParams.set(k, v);
    }
  });
  return url.toString();
};

// --- grids / moorings ---
export const getMooringsByGridId = async (gridId, token) => {
  const res = await fetch(`${API_URL}/grids/${gridId}/moorings/`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch moorings: ${res.status}`);
  return res.json();
};

export const getMooringById = async (mooringId, token) => {
  const res = await fetch(`${API_URL}/moorings/${mooringId}/`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch mooring: ${res.status}`);
  return res.json();
};

export const updateMooringTargetLocation = async (mooringId, latitude, longitude, token) => {
  const res = await fetch(`${API_URL}/moorings/${mooringId}/`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ target_latitude: latitude, target_longitude: longitude }),
  });
  if (!res.ok) throw new Error(`Failed to update mooring location: ${res.status}`);
  return res.json();
};

// --- positions (unchanged; assume endpoint exists) ---
export const getMooringPositionHistory = async (mooringId, startDate, endDate, token) => {
  const url = buildUrl(`/moorings/${mooringId}/positions/`, {
    start_date: startDate,
    end_date: endDate,
  });
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch mooring position history: ${res.status}`);
  return res.json();
};

// -------- NEW / UPDATED DATA ENDPOINTS --------

// Ticket #74 canonical endpoint: combined processed data (all packs) for a mooring
export const getMooringData = async (mooringId, { kinds = [], startDate, endDate } = {}, token) => {
  const url = buildUrl(`/mooring-data/${mooringId}/`, {
    kinds: kinds.length ? kinds.join(',') : undefined, // accepts CO2_K30 or CO2-K30 etc.
    start_date: startDate,
    end_date: endDate,
  });
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch mooring data: ${res.status}`);
  return res.json(); // returns serialized CleanedSensorRollup[] ordered by time
};

// Back-compat: old name mapped to new endpoint
export const getMooringRollup = async (mooringId, kinds = [], startDate, endDate, token) => {
  return getMooringData(mooringId, { kinds, startDate, endDate }, token);
};

// ViewSet action: /mooring-data/<id>/ (same data as above, different route)
export const getMooringCleanedRollups = async (
   mooringId,
   { kinds = [], startDate, endDate, intervalMin, agg } = {},
   token
 ) => {
   const url = buildUrl(`/mooring-data/${mooringId}/`, {
     kinds: kinds.length ? kinds.join(',') : undefined,
     start_date: startDate,
     end_date: endDate,
     interval_min: intervalMin,
     agg: agg,
   });
   const res = await fetch(url, { headers: authHeaders(token) });
   if (!res.ok) throw new Error(`Failed to fetch mooring cleaned-rollups: ${res.status}`);
   return res.json(); // CleanedSensorRollup[] ordered by time
};

// Generic filtered endpoints (useful for tables/exports with pagination)
export const getCleanedEvents = async (
  { mooring, sensorPack, depth, sensorKind, start, end, limit = 500, ordering = 'time' } = {},
  token
) => {
  const url = buildUrl(`/cleaned-events/`, {
    mooring,
    sensor_pack: sensorPack,
    depth,
    sensor_kind: sensorKind, // send CO2_K30 (underscored) ideally
    start,
    end,
    limit,
    ordering,
  });
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch cleaned-events: ${res.status}`);
  return res.json(); // DRF paginated response
};

export const getCleanedRollups = async (mooringId, { kinds = [], startDate, endDate, intervalMin, agg } = {}, token) => {
  const url = buildUrl(`/mooring-data/${mooringId}/`, {
    kinds: kinds.length ? kinds.join(',') : undefined,
    start_date: startDate,
    end_date: endDate,
    interval_min: intervalMin, // Critical: tells backend to downsample
    agg: agg               // Optional: tells backend how to aggregate (mean, max, etc)
  });
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch mooring cleaned-rollups: ${res.status}`);
  return res.json();
};

// --- gridded meteorology (RTMA/HRRR/MRMS) ---

const MET_PAGE_SIZE = 100;   // backend PageNumberPagination default; `limit` is ignored
const MET_MAX_PAGES = 200;
const MET_CONCURRENCY = 6;

export const PRECIP_1HR = 'precip_accum_1hr_grid';
export const PRECIP_DAILY = 'precip_accum_24hr_grid';

// Never plotted.
const HIDDEN_KINDS = new Set(['precip_rate_grid']);

const metFetchJson = async (url, token, attempts = 3) => {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await fetch(url, { headers: authHeaders(token) });
      if (res.status === 404) return null;          // past the last page
      if (!res.ok) throw new Error(`meteorology-rollups ${res.status}`);
      return await res.json();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) await new Promise(r => setTimeout(r, 300 * (i + 1)));
    }
  }
  throw lastErr;
};

const metPageUrl = (params, page) => {
  const url = new URL(`${API_URL}/meteorology-rollups/`);
  params.forEach((v, k) => url.searchParams.append(k, v));
  url.searchParams.set('page', page);
  url.searchParams.set('page_size', MET_PAGE_SIZE);   // ignored unless enabled; harmless
  return url.toString();
};

const mapWithLimit = async (items, limit, fn) => {
  const out = [];
  for (let i = 0; i < items.length; i += limit) {
    out.push(...await Promise.all(items.slice(i, i + limit).map(fn)));
  }
  return out;
};

/**
 * Walk every page of one meteorology query.
 *
 * The API uses PageNumberPagination, so `limit`/`offset` are ignored and page 1's
 * `count` tells us exactly how many pages exist. The server's `next` link is
 * deliberately unused: DRF builds it from the request, and behind the proxy it
 * comes back as http:// on an https page — blocked as mixed content in prod,
 * which is why this silently returned only page 1 there.
 */
const fetchAllMetPages = async (params, token) => {
  const first = await metFetchJson(metPageUrl(params, 1), token);
  if (first === null) return [];
  if (Array.isArray(first)) return first;

  const rows = first.results ?? [];
  const count = first.count ?? rows.length;
  const perPage = rows.length;
  if (perPage === 0 || rows.length >= count) return rows;

  const totalPages = Math.min(Math.ceil(count / perPage), MET_MAX_PAGES);
  const pageNums = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
  const rest = await mapWithLimit(pageNums, MET_CONCURRENCY,
    p => metFetchJson(metPageUrl(params, p), token));
  for (const page of rest) rows.push(...(page?.results ?? []));

  if (rows.length < count) {
    console.warn(`[Meteorology] truncated: ${rows.length}/${count} rows for `
      + `${params.get('sensor_kind') ?? 'all kinds'}`);
  }
  return rows;
};

/**
 * A 1-hour accumulation stamped T covers (T-1h, T], so the value landing exactly
 * on local midnight belongs to the day that just ended — hence the 1ms shift.
 */
const localDayKey = (iso, timeZone) => new Intl.DateTimeFormat('en-CA', {
  timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date(new Date(iso).getTime() - 1));

/**
 * Offset of `timeZone` from UTC at a given instant, in ms (positive east).
 */
const tzOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});

  const asUTC = Date.UTC(
    parts.year, parts.month - 1, parts.day,
    parts.hour % 24, parts.minute, parts.second,
  );
  return asUTC - date.getTime();
};

/**
 * The instant of local midnight starting 'YYYY-MM-DD' in `timeZone`.
 * Resolved twice so a day that begins on a DST boundary lands correctly.
 */
const localMidnightISO = (dayKey, timeZone) => {
  const naive = new Date(`${dayKey}T00:00:00Z`).getTime();
  let instant = naive - tzOffsetMs(new Date(naive), timeZone);
  instant = naive - tzOffsetMs(new Date(instant), timeZone);
  return new Date(instant).toISOString();
};

/**
 * Daily precip totals from hourly accumulations — one row per local day,
 * stamped at that day's local midnight.
 *
 * `hours` is the number of hourly values that fed the total. Anything under 24
 * is a partial day (window edges, or an outage) and is under-counted; the
 * consumer decides whether to plot, dim, or drop it.
 */
export const dailyPrecipTotals = (rows, { timeZone } = {}) => {
  const tz = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const hourly = rows.filter(r => r.sensor_kind === PRECIP_1HR && r.sensor_value != null);
  if (!hourly.length) return [];

  const units = new Set(hourly.map(r => r.unit));
  if (units.size > 1) console.warn(`[Meteorology] mixed precip units: ${[...units]}`);

  const byDay = new Map();
  for (const r of hourly) {
    const key = localDayKey(r.time, tz);
    const day = byDay.get(key) ?? { total: 0, hours: 0, sample: r };
    day.total += r.sensor_value;
    day.hours += 1;
    byDay.set(key, day);
  }

  return [...byDay.entries()]
    .map(([key, day]) => ({
      ...day.sample,
      id: `${PRECIP_DAILY}|${key}`,
      sensor_kind: PRECIP_DAILY,
      time: localMidnightISO(key, tz),
      local_day: key,
      hours: day.hours,
      sensor_value: Math.round(day.total * 1000) / 1000,
    }))
    .sort((a, b) => a.time.localeCompare(b.time));
};

/**
 * Fetch gridded meteorology for a mooring across the full window.
 *
 * Returns raw rows plus a derived `precip_accum_daily_grid` series — one point
 * per local day. `precip_rate_grid` is dropped. Hourly precip is fetched in its
 * own request WITHOUT `intervalMin`: bucketing an accumulation field with a mean
 * and then summing the buckets under-counts the daily total.
 */
export const getMeteorologyRollups = async (
  mooringId,
  { kinds = [], startDate, endDate, source, intervalMin, timeZone } = {},
  token,
) => {
  const base = new URLSearchParams({ mooring: mooringId });
  if (startDate) base.set('start', startDate);
  if (endDate) base.set('end', endDate);
  if (source) base.set('source', source);

  const main = new URLSearchParams(base);
  if (intervalMin) main.set('interval_min', intervalMin);
  kinds.filter(k => !HIDDEN_KINDS.has(k)).forEach(k => main.append('sensor_kind', k));

  const precip = new URLSearchParams(base);
  precip.set('sensor_kind', PRECIP_1HR);

  const [mainRows, precipRows] = await Promise.all([
    fetchAllMetPages(main, token),
    fetchAllMetPages(precip, token),
  ]);

  const byKey = new Map();
  for (const r of [...mainRows, ...precipRows]) {
    if (HIDDEN_KINDS.has(r.sensor_kind)) continue;
    byKey.set(`${r.sensor_kind}|${r.time}|${r.depth}|${r.source}`, r);
  }

  const rows = [...byKey.values()];
  return [...rows, ...dailyPrecipTotals(rows, { timeZone })]
    .sort((a, b) => a.time.localeCompare(b.time));
};

// --- HAB risk ---
export const getHabRiskLatest = async (mooringId, token) => {
  const res = await fetch(`${API_URL}/moorings/${mooringId}/hab-risk-latest/`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch HAB risk latest: ${res.status}`);
  return res.json();
};

export const getHabRisk7Day = async (mooringId, token) => {
  const res = await fetch(`${API_URL}/moorings/${mooringId}/hab-risk-7day/`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch HAB risk 7-day: ${res.status}`);
  return res.json();
};

export const getHabRiskAllLatest = async (token) => {
  const res = await fetch(`${API_URL}/hab-risk/?latest=true`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`Failed to fetch all HAB risk: ${res.status}`);
  return res.json();
};

// --- installations ---
export const getMooringInstallations = async (mooringId, token) => {
  try {
    // Ensure path has trailing slash
    const url = buildUrl('/installations/', { mooring: mooringId });
    const res = await fetch(url, { headers: authHeaders(token) });
    
    if (!res.ok) {
      // If 404 or 403, return empty list instead of throwing hard error
      if (res.status === 404 || res.status === 403) return []; 
      throw new Error(`API Error: ${res.status}`);
    }
    return await res.json();
  } catch (err) {
    console.error("Error in getMooringInstallations:", err);
    throw err; // Allow the UI to handle it (or ignore it as we did in Step 1)
  }
};